/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  DatePicker,
  InputNumber,
  message,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import moment from 'moment';

// Utils
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';

// Components
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';

// APIs (Asegúrate de haber creado estas funciones en tu archivo de API)
import {
  getIncidenciasReloj,
  getIncidenciaReloj,
  postIncidenciaReloj,
  patchIncidenciaReloj,
  deleteIncidenciaReloj,
  autorizarIncidenciaReloj,
} from 'api/catalogos/incidenciasReloj';

const SolicitudesIncidenciasReloj = ({
  selected, // ID de la Solicitud Padre
  permission,
  estatusInactivo,
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [data, setData] = useState([]);
  const [tiposIncidencias, setTiposIncidencias] = useState([]);
  const [selectedData, setSelectedData] = useState({});
  const [estatusVisual, setEstatusVisual] = useState('Activo');

  const fetchData = async () => {
    setLoading(true);
    try {
      if (selected) {
        const _data = await getIncidenciasReloj({ solicitud: selected });
        setData(formatReceived(_data));
      }
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      if (selected) {
        setLoading(true);
        try {
          const [_tipos, _data] = await Promise.all([
            // getTiposEvaluacionAsistencia({ estados_globales: ESTATUS_AUTORIZADO }),
            getIncidenciasReloj({ solicitud: selected }),
          ]);
          if (mounted) {
            setTiposIncidencias(_tipos);
            setData(formatReceived(_data));
          }
        } catch (error) {
          onError(error);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, [selected]);

  const onCancel = () => {
    setVisible(false);
    setSelectedRowKeys([]);
    setSelectedData({});
    setEstatusVisual('Activo');
    form.resetFields();
  };

  const onClickEdit = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    try {
      const _res = await getIncidenciaReloj(key);
      if (_res) {
        const values = formatReceived(_res);
        setEstatusVisual(values.vigencia_final ? 'Inactivo' : 'Activo');
        form.setFieldsValue(values);
        setVisible(true);
      }
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const [key] = selectedRowKeys;
      const payload = {
        ...values,
        solicitud: selected,
        vigencia_final: values.vigencia_final ? values.vigencia_final.format('YYYY-MM-DD') : null,
      };

      const saved = await (key ? patchIncidenciaReloj(key, payload) : postIncidenciaReloj(payload));
      if (saved) {
        onCancel();
        await fetchData();
      }
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      titleText: 'Incidencia',
      dataIndex: 'incidencia_nombre', // Ajustar según serializer
      key: 'incidencia',
      width: 250,
    },
    {
      titleText: 'Tolerancia',
      dataIndex: 'tolerancia',
      key: 'tolerancia',
      width: 100,
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigencia_final',
      key: 'vigencia_final',
      width: 150,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : '-'),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estatus_descripcion',
      key: 'estatus',
      width: 150,
    },
  ];

  return (
    <Row style={{ marginTop: '20px' }}>
      <Col span={24}>
        <p style={{ color: '#D88F4F', fontWeight: 600 }}>Incidencias de Reloj Checador</p>
        <Table
          cols={columns}
          data={data}
          rowSelection={{ selectedRowKeys, type: 'radio', onChange: (keys) => setSelectedRowKeys(keys) }}
          handleOnRowClick={(record) => {
            setSelectedRowKeys([record.id]);
            setSelectedData(record);
          }}
          controls={{
            onClickAdd: () => { form.resetFields(); setVisible(true); },
            onClickEdit,
            onClickDelete: () => setVisibleAlert(true),
          }}
          customActions={[
            {
              onClick: () => setVisibleAutorizar(true),
              permission: { model: 'incidenciarelojchecador', action: 'autorizar' },
              icon: <CheckCircleOutlined />,
              text: 'Autorizar',
              disabled: selectedData?.estados_globales !== ESTATUS_ACTIVO,
              className: 'success',
            },
          ]}
          disableAdd={estatusInactivo}
          disableDelete={selectedData?.estados_globales === ESTATUS_AUTORIZADO}
          permission={permission}
        />
      </Col>

      <Modal
        open={open}
        footer={null}
        width="600px"
        title={(
          <FormSubmitControls
            label="Incidencia de Reloj Checador"
            onFinish={onFinish}
            onCancel={onCancel}
          />
)}
      >
        <Form layout="vertical" form={form} initialValues={{ tolerancia: 0 }}>
          <Form.Item
            name="incidencia"
            label="Incidencia"
            rules={[{ required: true }]}
            extra="Campo vinculado a la tabla de Tipos de Evaluación de Asistencia"
          >
            <Select dataSource={tiposIncidencias} labelProp="nombre" keyProp="id" />
          </Form.Item>

          <Form.Item
            name="tolerancia"
            label="Tolerancia"
            rules={[{ required: true }]}
            extra="Campo numérico, debe admitir cero."
          >
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>

          <Form.Item
            name="vigencia_final"
            label="Vigencia Final"
            extra="Campo de Fecha de captura manual"
          >
            <DatePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              onChange={(date) => setEstatusVisual(date ? 'Inactivo' : 'Activo')}
            />
          </Form.Item>

          <Form.Item label="Estatus" extra="El registro nace Activo, al Autorizar cambia a Autorizado.">
            <Input value={estatusVisual} disabled />
          </Form.Item>
        </Form>
      </Modal>

      <ModalDelete
        open={visibleAlert}
        onDelete={async () => {
          await deleteIncidenciaReloj(selectedRowKeys[0]);
          setVisibleAlert(false);
          fetchData();
        }}
        onCancel={() => setVisibleAlert(false)}
      />

      <ModalConfirmation
        open={visibleAutorizar}
        ConfirmAutorize
        onFinish={async () => {
          await autorizarIncidenciaReloj(selectedRowKeys[0]);
          setVisibleAutorizar(false);
          fetchData();
        }}
        onCancel={() => setVisibleAutorizar(false)}
      />
    </Row>
  );
};

export default SolicitudesIncidenciasReloj;
